/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-lines */
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import Tooltip from '@base/Tooltip/Tooltip';
// import H4 from '@base/H4/H4';
import { useSelector } from 'react-redux';
import { getAccessToken } from '@selectors/auth';
import { segment } from '@actions/analytics';
import {
  DOW2_INVALID_DOUBT,
  DOW2_SOLUTION_SENT,
  DOW2_SUBMIT_TO_EDUCATOR,
  DOW2_TIMEOUT
} from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { getLmpPlayerURL } from '@api/selectors/urls';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { captureException } from '@utils/sentry';
import { SubmitForReviewError } from '@utils/customErrors';
import { goBack } from '@utils/breadcrumbs';
import useURLs from '@api/hooks/useURLs';
import { getSelectedDoubtSolution } from '@api/selectors/doubts';
import useDoubtConfig from '@api/hooks/goal/custom-doubts/useDoubtConfig';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getMeUid, getMeUserId } from '@api/selectors/auth';
import useCustomDoubtsStore from '@stores/customDoubtsStore';
import {
  getInstantMatchFlagSetter,
  getLimitFlag,
  getLimitFlagSetter,
  getInstantMatchFlag,
  getSubjectSelectionFlowSetter,
  getSubjectSelectionFlow,
  getSelectedFeedbackChoice,
  getSubmittedFeedbacks,
  getIsSubmittingSetter,
  getSelectedDoubtSolutionSetter
} from '@stores/selectors/customDoubtSelectors';
import { useToggleable } from '@stores/toggleables';
import { DOUBTS_FEEDBACK_DRAWER, LOGIN_DRAWER } from '@constants/toggleables';
import useFeedBackChoices from '@api/hooks/goal/custom-doubts/useFeedBackChoices';
import useDoubtDetailsWithoutAuth from '@api/hooks/goal/custom-doubts/useDoubtDetailsWithoutAuth';
import useDoubtDetails from '@api/hooks/goal/custom-doubts/useDoubtDetails';
import Dynamic from '@base/Dynamic/Dynamic';
import { postReviewRequest } from '@api/fetchers/goal/custom-doubts';
import { getIsSubscribedToOffline } from '@selectors/subscription';
import TopBlock from './TopBlock';
import SideBar from './SideBar';
import DoubtStateCard from './DoubtStateCard';
import {
  ASK_AN_EDUCATOR,
  DOUBT_STATE,
  VIDEO_RECORDING_STATE,
  subjectSelectionState
} from './constants';
import { dialogImage } from './CommonComponents';
import DoubtSolutionPlayer from './DoubtSolutionPlayer';
import { getHeadingMessageImageUrl, showLimitFlag } from '../utils/limitDailog';
import defaultExtraProps from '../utils/defaultExecptionValues';

const FeedbackCard = Dynamic(() => import('./FeedbackCard'));
const PostFeedbackCard = Dynamic(() => import('./PostFeedbackCard'));
const AskAEducator = Dynamic(() => import('./AskAEducator'));
const IconicUpgradeCard = Dynamic(() => import('./IconicUpgradeCard'));
const LimitDialog = Dynamic(() => import('../LimitDialog'));
const LeavePageDailog = Dynamic(() => import('./LeavePageDailog'));
const LoginBanner = Dynamic(() => import('./LoginBanner'));
const NegativeFeebackDrawer = Dynamic(() => import('./NegativeFeebackDrawer'));
const AskAEducatorDailog = Dynamic(() => import('./AskAEducatorDailog'));
const ReviewSubmittedDialog = Dynamic(() => import('./ReviewSubmittedDialog'));
const SubjectSelectionDrawer = Dynamic(() =>
  import('./SubjectSelectionDrawer')
);

const StyTooltip = styled(Tooltip)`
  &.MuiTooltip-popper {
    left: 50% !important;
    top: 0px !important;
    position: absolute;
    z-index: 1;
    transform: translate3d(0px, 60vh, 10px) !important;
  }
  & {
    .MuiTooltip-tooltip {
      font-weight: ${Fonts.SEMIBOLD};
      font-size: ${Fonts.SMALL};
    }
    text-align: center;
  }
`;

const StyDoubtSolutionPlayer = styled(DoubtSolutionPlayer)`
  border-radius: ${({ showBottomBorderRadius }) =>
    showBottomBorderRadius
      ? `${Spacings.SPACING_2B}`
      : `${Spacings.SPACING_2B} ${Spacings.SPACING_2B} 0px 0px`};
  box-shadow: ${boxShadowGenerator()};
  ${mediaQuery.uptoMobile} {
    border-radius: ${Spacings.SPACING_1B};
    height: 450px;
  }
`;

const RightBar = styled.div`
  margin-top: ${Spacings.SPACING_6B};
  grid-column: 1/15;
  display: flex;
  flex-direction: column;
  ${mediaQuery.uptoMobile} {
    align-items: center;
  }
`;

const SeeSolution = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    max-width: 360px;
  }
`;

const MAX_POLLING_COUNT = 7;

const DOUBT_STATUS = {
  SOLUTION_PENDING: 'SOLUTION_PENDING',
  INSTANT_MATCH_IN_PROGRESS: 'INSTANT_SOLUTION_IN_PROGRESS',
  INVALID_DOUBT: 'INVALID_CONTENT',
  TIMEOUT: 'TIMEOUT',
  SOLUTION_AVAILABLE: 'SOLUTION_AVAILABLE',
  SOLVED: 'SOLVED',
  UNDER_EDUCATION_REVIEW: 'UNDER_EDUCATOR_REVIEW',
  NO_ISSUES_FOUND_WHILE_REVIEW: 'NO_ISSUES_FOUND_WHILE_REVIEW',
  ASD_AVAILABLE: 'COMPLETED',
  ASD_FAILED: 'FAILED',
  ASD_PENDING: 'INPROGRESS',
  ASD_NOT_SUPPORTED: 'NOT_SUPPORTED'
};

const DoubtSolution = ({ goalUID, goalInfo }) => {
  const { query } = useRouter();
  const { doubtUID } = query;
  const { doubtDetailsData: doubtDetailsWithoutAuth } =
    useDoubtDetailsWithoutAuth(goalUID, doubtUID);
  const userUID = useMyInfo(getMeUserId);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const isUserSpecificViewAllowed =
    isLoggedIn && userUID === doubtDetailsWithoutAuth.doubtOwnerUID;
  const {
    doubtDetailsData: doubtDetailsWithAuth,
    mutate: revalidateDoubtDetails
  } = useDoubtDetails(goalUID, doubtUID, { isSelected: false });
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const doubtDetails = isUserSpecificViewAllowed
    ? doubtDetailsWithAuth
    : doubtDetailsWithoutAuth;
  const {
    state,
    solutions = [],
    subscription,
    askToEducator,
    is_sample,
    asdState,
    allowSubjectUpdate,
    subject
  } = doubtDetails;
  const [isSubjectBeingChanged, setIsSubjectBeingChanged] = useState(false);
  const { isOpen: drawerState } = useToggleable(DOUBTS_FEEDBACK_DRAWER);
  const isInstantMatch = useCustomDoubtsStore(getInstantMatchFlag);
  const setInstantMatch = useCustomDoubtsStore(getInstantMatchFlagSetter);
  const setIsSubmitting = useCustomDoubtsStore(getIsSubmittingSetter);
  const updateSubjectSelectionState = useCustomDoubtsStore(
    getSubjectSelectionFlowSetter
  );
  const setSelectedDoubtSolution = useCustomDoubtsStore(
    getSelectedDoubtSolutionSetter
  );
  const selectedSolutionUID = useCustomDoubtsStore(getSelectedDoubtSolution);
  const selectedSolution = useMemo(
    () => solutions?.find((sol) => sol.uid === selectedSolutionUID) || {},
    [solutions, selectedSolutionUID]
  );

  const timerID = useRef();
  // let CURRENT_POLLING_COUNT = 0;
  const [CURRENT_POLLING_COUNT, SET_CURRENT_POLLING_COUNT] = useState(0);
  const setPolling = () => {
    timerID.current = setInterval(() => {
      revalidateDoubtDetails();
      SET_CURRENT_POLLING_COUNT((prev) => prev + 1);
    }, 5000);
  };

  const limitFlag = useCustomDoubtsStore(getLimitFlag);
  const setLimitFlag = useCustomDoubtsStore(getLimitFlagSetter);
  const {
    doubtConfigData: {
      dailyRemainingDoubtsCount,
      dailyTotalDoubtsCount,
      dailyRemainingOpenDoubtsCount,
      dailyTotalOpenDoubtsCount
    }
  } = useDoubtConfig(goalUID);
  useEffect(() => {
    if (isLoggedIn) {
      setPolling();
    }
    return () => clearInterval(timerID.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shouldAskForFeedback =
    selectedSolution?.should_ask_for_feedback ||
    selectedSolution?.shouldAskForFeedback;
  const objectType =
    selectedSolution?.object_type || selectedSolution?.objectType;

  const feedbackSubmitted =
    useCustomDoubtsStore(getSubmittedFeedbacks(doubtUID))?.[
      selectedSolutionUID
    ] || selectedSolution?.userFeedback?.intent;

  useEffect(() => {
    if (!asdState) return;
    if (
      CURRENT_POLLING_COUNT >= MAX_POLLING_COUNT ||
      (asdState !== DOUBT_STATUS.ASD_PENDING &&
        state !== DOUBT_STATUS.INSTANT_MATCH_IN_PROGRESS) ||
      (asdState === DOUBT_STATE.ASD_FAILED && !subject?.uid)
    ) {
      clearInterval(timerID.current);
      SET_CURRENT_POLLING_COUNT(0);
    }
  }, [CURRENT_POLLING_COUNT, asdState, state, allowSubjectUpdate]);

  useEffect(() => {
    const uid = doubtDetails?.solutions?.[0]?.uid ?? null;
    setSelectedDoubtSolution(uid);
  }, [doubtDetails?.solutions?.[0]?.uid]);

  const doubtKeyObj = {
    [DOUBT_STATUS.INSTANT_MATCH_IN_PROGRESS]: 'findingSolution',
    [DOUBT_STATUS.SOLUTION_PENDING]: 'doubtSubmitted',
    [DOUBT_STATUS.INVALID_DOUBT]: 'invalidDoubt',
    [DOUBT_STATUS.TIMEOUT]: 'noSolutionAvailable',
    [DOUBT_STATUS.ASD_AVAILABLE]: 'asdCompleted',
    [DOUBT_STATUS.ASD_FAILED]: 'asdFailed',
    [DOUBT_STATUS.ASD_PENDING]: 'asdPending',
    [DOUBT_STATUS.SOLUTION_AVAILABLE]: 'availableSolution'
  };

  let doubtKey = doubtKeyObj[state];
  if (asdState === DOUBT_STATUS.ASD_PENDING) {
    doubtKey = doubtKeyObj[DOUBT_STATUS.INSTANT_MATCH_IN_PROGRESS];
  }

  const solutionArray = [
    DOUBT_STATUS.SOLUTION_AVAILABLE,
    DOUBT_STATUS.SOLVED,
    DOUBT_STATUS.UNDER_EDUCATION_REVIEW,
    DOUBT_STATUS.NO_ISSUES_FOUND_WHILE_REVIEW
  ];

  const isSolutionAvailabe =
    asdState !== DOUBT_STATUS.ASD_PENDING &&
    (solutionArray.includes(state) || solutions?.length > 0);

  const routeGoalSegmentData = useGoalEventProps({ goalUID });
  const recordingURL = useURLs(getLmpPlayerURL);
  const accessToken = useSelector(getAccessToken);
  const isOfflineSubscription = useSelector(getIsSubscribedToOffline(goalUID));

  const getExtraDataForPlayer = (selectedUid) => {
    const newSelectedSolution =
      solutions?.find((sol) => sol.uid === selectedUid) || {};
    const { content = {} } = newSelectedSolution?.attachments?.[0] || {};
    const { url } = content;
    if (!url) return {};
    const matchedData = url.match(/&uid=.*/);
    if (!matchedData) return {};
    const uid = matchedData[0].split('_')[1];
    return { state: VIDEO_RECORDING_STATE, uid, video_url: url };
  };
  const extraLiveClassData = useMemo(
    () => getExtraDataForPlayer(selectedSolutionUID),
    [selectedSolutionUID]
  );

  let liveClassData = {
    isDarkModeOn: true,
    videoSolutionData: {
      access_token: accessToken,
      api_host: process.env.BACKEND_URL.slice(0, -1),
      object_uid: selectedSolutionUID,
      object_type: objectType,
      is_combat: false,
      submit_new_solution: false,
      shouldAutoplay: true,
      is_live_mentoring: false,
      replay: true,
      isLMPReplay: false,
      role: 'listener',
      inlinePlayer: true,
      ...(extraLiveClassData || {})
    }
  };

  const onPlayerReady = () => {};

  const onSolutionClick = (uid) => {
    const extraData = getExtraDataForPlayer(uid);
    liveClassData = {
      isDarkModeOn: true,
      videoSolutionData: {
        access_token: accessToken,
        api_host: process.env.BACKEND_URL.slice(0, -1),
        object_uid: uid,
        object_type: objectType,
        is_combat: false,
        submit_new_solution: false,
        shouldAutoplay: true,
        is_live_mentoring: false,
        replay: true,
        isLMPReplay: false,
        role: 'listener',
        inlinePlayer: true,
        ...(extraData || {})
      }
    };
    document
      .getElementById('solutionCanvas')
      ?.contentWindow?.postMessage(liveClassData, '*');
  };
  const { feedbackChoices = [] } = useFeedBackChoices(goalUID);
  const selectedChoice = useCustomDoubtsStore(getSelectedFeedbackChoice);
  const selectedChoiceObj = feedbackChoices.find(
    (choice) => choice.uid === selectedChoice
  );

  const myUserUID = useMyInfo(getMeUid);
  const subjectSelectedStateFromStore = useCustomDoubtsStore(
    getSubjectSelectionFlow(goalUID, doubtUID)
  );
  const [reviewDialog, setReviewDialog] = useState(false);
  const [reviewSubmittedDialog, setReviewSubmittedDialog] = useState(false);

  const handleReviewDialogSubmit = () => {
    setIsSubmitting(true);
    postReviewRequest(doubtUID, goalUID, [])
      .then((res) => {
        if (res?.success) {
          revalidateDoubtDetails();
          segment.track(DOW2_SUBMIT_TO_EDUCATOR, {
            doubt_id: doubtUID,
            feature_version: 2,
            solution_feedback_choices: [selectedChoiceObj?.text],
            ...routeGoalSegmentData
          });
          setReviewDialog(false);
          setReviewSubmittedDialog(true);
        } else {
          setReviewDialog(false);
          const defaultExtraPropsObj = defaultExtraProps(myUserUID, res?.error);
          const extraProps = {
            type: 'tag',
            key: 'doubtUID',
            value: doubtUID
          };
          const combinedProps = [...defaultExtraPropsObj, extraProps];
          captureException(
            new SubmitForReviewError('Submit For Review Error', res),
            null,
            null,
            combinedProps
          );
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleReviewDialogClose = () => {
    setReviewDialog(false);
  };

  const handleReviewSubDialogClose = () => {
    setReviewSubmittedDialog(false);
  };
  // on Click for submit for review
  const handleSubmitForReview = () => {
    if (!isLoggedIn) {
      return openLoginDrawer();
    }
    if (
      showLimitFlag(dailyRemainingDoubtsCount, dailyRemainingOpenDoubtsCount)
    ) {
      setLimitFlag(true);
      return;
    }
    setReviewDialog(true);
  };

  const { heading, message, imgSrc, msgWidth, statusCode } =
    getHeadingMessageImageUrl(
      dailyRemainingDoubtsCount,
      dailyRemainingOpenDoubtsCount,
      dailyTotalDoubtsCount,
      dailyTotalOpenDoubtsCount
    );

  const onCloseModal = () => {
    setLimitFlag(false);
    if (
      dailyRemainingDoubtsCount !== 0 &&
      dailyRemainingOpenDoubtsCount !== 0
    ) {
      setIsSubmitting(true);
      postReviewRequest(doubtUID, goalUID, [])
        .then((res) => {
          if (res?.success) {
            revalidateDoubtDetails();
            segment.track(DOW2_SUBMIT_TO_EDUCATOR, {
              doubt_id: doubtUID,
              feature_version: 2,
              solution_feedback_choices: [selectedChoiceObj?.text],
              ...routeGoalSegmentData
            });
          } else {
            const defaultExtraPropsObj = defaultExtraProps(
              myUserUID,
              res.result
            );
            const extraProps = {
              type: 'tag',
              key: 'doubtUID',
              value: doubtUID
            };
            const combinedProps = [...defaultExtraPropsObj, extraProps];
            captureException(
              new SubmitForReviewError('Submit For Review Error', res),
              null,
              null,
              combinedProps
            );
          }
        })
        .finally(() => setIsSubmitting(false));
    }
  };

  const getAskAEducatorCardDetails = () => {
    // eslint-disable-next-line no-nested-ternary
    return askToEducator
      ? ASK_AN_EDUCATOR.askEducator
      : state === DOUBT_STATUS.UNDER_EDUCATION_REVIEW
      ? ASK_AN_EDUCATOR.doubtSubmitted
      : ASK_AN_EDUCATOR.doubtReviewed;
  };

  const sendEvent = (eventName, eventProps) => {
    segment.track(eventName, {
      doubt_type: 'Ask A Doubt',
      ...eventProps,
      ...routeGoalSegmentData
    });
  };

  // useEffect for sending event on state change of doubt
  useEffect(() => {
    if (state === DOUBT_STATUS.SOLUTION_AVAILABLE) {
      sendEvent(DOW2_SOLUTION_SENT, {
        is_instant_match: isInstantMatch,
        number_of_solutions: solutions?.length,
        solution_uid: solutions?.map((sol) => sol.uid),
        feature_version: 2,
        doubt_subject_selection:
          subjectSelectedStateFromStore ||
          subjectSelectionState.defaultSubjectSelection,
        flow_type: 'Non_Demo'
      });
      setInstantMatch(false);
    } else if (state === DOUBT_STATUS.INVALID_DOUBT) {
      sendEvent(DOW2_INVALID_DOUBT, {
        doubt_id: doubtUID
      });
    } else if (state === DOUBT_STATUS.TIMEOUT) {
      sendEvent(DOW2_TIMEOUT, {
        doubt_id: doubtUID
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
  const [leavePageDailog, setLeavePageDialog] = useState(false);
  const hanldeBackButton = () => {
    if (state === DOUBT_STATUS.INSTANT_MATCH_IN_PROGRESS)
      setLeavePageDialog(true);
    else {
      goBack();
    }
  };

  const handleLeave = () => {
    goBack();
  };

  const asdDrawerShown = asdState === DOUBT_STATUS.ASD_FAILED && !subject?.uid;

  const [subjectDrawer, setSubjectDrawer] = useState(asdDrawerShown);

  useEffect(() => {
    if (asdDrawerShown) {
      clearInterval(timerID.current);
      setSubjectDrawer(true);
    }
  }, [asdState, asdDrawerShown]);
  const threeDotsShown =
    asdState !== DOUBT_STATUS.ASD_PENDING && allowSubjectUpdate;

  const [toolTip, setToolTip] = useState(false);
  const handleManualSubjectUpdate = () => {
    setPolling();
    updateSubjectSelectionState(
      goalUID,
      doubtUID,
      subjectSelectionState.manualAfterAsdFailed
    );
  };

  return (
    <>
      <TopBlock
        goalData={goalInfo}
        goalUID={goalUID}
        onBackClick={hanldeBackButton}
      />
      <StyTooltip
        arrow={false}
        open={toolTip}
        title="Doubt subject updated"
        placement="bottom"
        size="183px"
        borderRadius={Spacings.SPACING_2B}
        maxWidth="183px"
        padding={Spacings.SPACING_4B}
      >
        <div />
      </StyTooltip>
      <RightBar>
        {isSolutionAvailabe && subject.uid ? (
          <SeeSolution>
            <StyDoubtSolutionPlayer
              liveClassData={liveClassData}
              doubtUID={doubtUID}
              solutionUID={selectedSolutionUID}
              goalUID={goalUID}
              onReady={onPlayerReady}
              is_sample={is_sample}
              recordingURL={recordingURL}
              showBottomBorderRadius={
                !shouldAskForFeedback && !feedbackSubmitted
              }
              subject={subject}
              revalidateDoubtDetails={revalidateDoubtDetails}
              isUserSpecificViewAllowed={isUserSpecificViewAllowed}
            />
            {shouldAskForFeedback && isUserSpecificViewAllowed ? (
              <FeedbackCard
                goalUID={goalUID}
                doubtUID={doubtUID}
                solutionUID={selectedSolutionUID}
                onSolutionClick={onSolutionClick}
                subject={subject}
                revalidateDoubtDetails={revalidateDoubtDetails}
              />
            ) : null}
            {!shouldAskForFeedback &&
            feedbackSubmitted &&
            isUserSpecificViewAllowed ? (
              <PostFeedbackCard
                goalUID={goalUID}
                doubtUID={doubtUID}
                onSolutionClick={onSolutionClick}
              />
            ) : null}
            {isUserSpecificViewAllowed &&
              (askToEducator ||
                state === DOUBT_STATUS.UNDER_EDUCATION_REVIEW ||
                state === DOUBT_STATUS.NO_ISSUES_FOUND_WHILE_REVIEW) && (
                <AskAEducator
                  goalUID={goalUID}
                  cardDetails={getAskAEducatorCardDetails()}
                  onClick={handleSubmitForReview}
                  doubtUID={doubtUID}
                />
              )}
          </SeeSolution>
        ) : null}

        {!subject?.uid || !isSolutionAvailabe ? (
          <DoubtStateCard cardDetails={DOUBT_STATE[doubtKey]} />
        ) : null}
        {!isOfflineSubscription &&
          subscription?.upgrade_to_iconic &&
          isUserSpecificViewAllowed && (
            <IconicUpgradeCard subscription={subscription} goalUID={goalUID} />
          )}
        {drawerState && isUserSpecificViewAllowed && (
          <NegativeFeebackDrawer
            goalUID={goalUID}
            doubtUID={doubtUID}
            solutionUID={selectedSolutionUID}
            subject={subject}
            revalidateDoubtDetails={revalidateDoubtDetails}
          />
        )}
      </RightBar>
      <SideBar
        threeDotsShown={threeDotsShown}
        doubtDetails={doubtDetails}
        isUserSpecificViewAllowed={isUserSpecificViewAllowed}
        goalUID={goalUID}
        onSolutionClick={onSolutionClick}
        setToolTip={setToolTip}
        subject={subject}
        setIsSubjectBeingChanged={setIsSubjectBeingChanged}
        isSubjectBeingChanged={isSubjectBeingChanged}
        revalidateDoubtDetails={revalidateDoubtDetails}
        ocrText={doubtDetailsWithoutAuth?.ocrText}
      />
      {!isLoggedIn && <LoginBanner />}
      {isLoggedIn && subjectDrawer && isUserSpecificViewAllowed ? (
        <SubjectSelectionDrawer
          subjectDrawer={subjectDrawer}
          selectedSubject={null}
          setSubjectDrawer={setSubjectDrawer}
          doubtUID={doubtUID}
          goalUID={goalUID}
          hideCloseButton
          onComplete={handleManualSubjectUpdate}
          state={subjectSelectionState.manualAfterAsdFailed}
          setIsSubjectBeingChanged={setIsSubjectBeingChanged}
        />
      ) : null}
      {limitFlag && isUserSpecificViewAllowed && (
        <LimitDialog
          heading={heading}
          message={message}
          imgComp={dialogImage(imgSrc)}
          statusCode={statusCode}
          onCloseModal={onCloseModal}
          msgWidth={msgWidth}
          goalUID={goalUID}
          askAnEducator
        />
      )}
      {leavePageDailog && isUserSpecificViewAllowed && (
        <LeavePageDailog
          dialogState={leavePageDailog}
          handleLeave={handleLeave}
          handleWait={() => setLeavePageDialog(false)}
          imgComp={dialogImage('doubts20/lastDoubt.svg')}
        />
      )}
      {reviewDialog && isUserSpecificViewAllowed && (
        <AskAEducatorDailog
          onSubmit={handleReviewDialogSubmit}
          onClose={handleReviewDialogClose}
          flag={reviewDialog}
        />
      )}
      {reviewSubmittedDialog && isUserSpecificViewAllowed && (
        <ReviewSubmittedDialog
          onClose={handleReviewSubDialogClose}
          flag={reviewSubmittedDialog}
        />
      )}
    </>
  );
};

export default DoubtSolution;
